package diana.soleil.design.iraniancanadienbazzar.feature.business

import androidx.compose.foundation.background
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import diana.soleil.design.iraniancanadienbazzar.core.data.LocationController
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.StatusBarScrim
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppCard
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppChip
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.BusinessCover
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.BusinessListItem
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.MapPin
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.MapPoint
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppMap
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.categoryImageRes
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppBottomSheet
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.map_same_spot_title
import androidx.compose.material3.Text
import diana.soleil.design.iraniancanadienbazzar.core.model.Business
import diana.soleil.design.iraniancanadienbazzar.core.model.GeoPoint
import diana.soleil.design.iraniancanadienbazzar.core.model.categoryIconKey
import diana.soleil.design.iraniancanadienbazzar.core.model.distanceKm
import diana.soleil.design.iraniancanadienbazzar.core.model.distanceKmFrom
import diana.soleil.design.iraniancanadienbazzar.core.model.location
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_badge_new
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.map_attribution
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.map_cluster_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.map_back_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.map_pin_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.map_radius_km
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.map_recenter_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_distance_km
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_distance_m
import diana.soleil.design.iraniancanadienbazzar.core.model.formatCount
import diana.soleil.design.iraniancanadienbazzar.core.model.cityLabel
import kotlin.math.roundToInt
import org.jetbrains.compose.resources.stringResource
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsEvents
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsParams
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsTracker
import diana.soleil.design.iraniancanadienbazzar.core.data.CityController
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel

/**
 * Stateful entry for the "Explore on map" screen: metro-scoped businesses from [MapViewModel],
 * live device location from [LocationController] (requested on open — a maps screen is the most
 * contextual moment to ask). The camera recenters once when a location arrives.
 */
@Composable
fun MapRoute(
    onBack: () -> Unit,
    onBusinessClick: (String) -> Unit,
    modifier: Modifier = Modifier,
    viewModel: MapViewModel = koinViewModel(),
    locationController: LocationController = koinInject(),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    val userLocation by locationController.location.collectAsStateWithLifecycle()
    val analytics = koinInject<AnalyticsTracker>()
    val cityController = koinInject<CityController>()
    LaunchedEffect(Unit) {
        analytics.track(AnalyticsEvents.MAP_OPEN, mapOf(AnalyticsParams.METRO_ID to (cityController.activeMetroId.value ?: "all")))
        locationController.ensureLocation()
    }
    MapScreen(
        businesses = state.businesses,
        metroCenter = state.metroCenter,
        hasActiveMetro = state.hasActiveMetro,
        userLocation = userLocation,
        onBack = onBack,
        onBusinessClick = onBusinessClick,
        modifier = modifier,
    )
}

/** Radius choices for "Iranian businesses near me" (km). 5 km is the default per owner direction. */
private val RADIUS_OPTIONS_KM = listOf(1.0, 5.0, 10.0)
private const val DEFAULT_RADIUS_KM = 5.0

/** Country-wide starting zoom for "All Canada"/"All USA", where no metro sets the scale. */
private const val ALL_REGIONS_ZOOM = 4.0

/**
 * Metro-wide starting zoom, used when browsing a metro without a location fix. A "metro" here is
 * 30-50 km across (the GTA runs Oakville to Markham), so the radius presets can't frame one.
 */
private const val METRO_ZOOM = 9.5

/** Pin budget when no radius applies. Above this the map stutters hard enough to risk an ANR. */
private const val MAX_PINS_WITHOUT_RADIUS = 150

/** A fix farther than this from the metro center means the user isn't browsing from there. */
private const val METRO_VICINITY_KM = 150.0

/** Zoom presets sized so the selected radius circle roughly fills the viewport. */
private fun zoomFor(radiusKm: Double): Double = when {
    radiusKm <= 1.0 -> 14.5
    radiusKm <= 5.0 -> 12.5
    else -> 11.5
}

/**
 * Full-screen directory map: every geocoded business in the active metro within [radius] of the
 * user (falling back to the metro center before/without a location fix). Pin tap → floating
 * business card → detail. Stateless over the data; camera + selection are local UI state.
 */
@Composable
fun MapScreen(
    businesses: List<Business>,
    metroCenter: GeoPoint,
    hasActiveMetro: Boolean,
    userLocation: GeoPoint?,
    onBack: () -> Unit,
    onBusinessClick: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    val lang = AppTheme.language
    var radiusKm by remember { mutableStateOf(DEFAULT_RADIUS_KM) }
    var selectedId by remember { mutableStateOf<String?>(null) }
    // The radius anchors to the user only when they're actually IN the browsed metro; someone
    // browsing GTA from Vancouver (or an emulator in California) must still see GTA businesses
    // around the metro center, not an empty circle over their sofa.
    val userInMetro = hasActiveMetro && userLocation != null &&
        distanceKm(userLocation, metroCenter) <= METRO_VICINITY_KM
    // "All Canada"/"All USA" has no meaningful centre. Anchoring it on the placeholder (Toronto)
    // put every business outside downtown Toronto beyond the radius, so a Vancouver member browsing
    // All Canada opened the map to an empty circle over a city they don't live in. With no metro
    // and no usable fix, aim at the middle of the data that actually loaded instead.
    val dataCenter = remember(businesses) {
        businesses.mapNotNull { it.location() }.takeIf { it.isNotEmpty() }?.let { points ->
            GeoPoint(points.sumOf { it.lat } / points.size, points.sumOf { it.lng } / points.size)
        }
    }
    // Aim at where the businesses actually are, not at a city-hall point. A metro centre is a poor
    // proxy: the GTA's businesses sit across North York, Markham, Richmond Hill and Oakville, tens
    // of km from downtown Toronto.
    val anchor = when {
        userInMetro && userLocation != null -> userLocation
        else -> dataCenter ?: metroCenter
    }
    var center by remember { mutableStateOf(MapPoint(anchor.lat, anchor.lng)) }
    // A nationwide view needs to start zoomed out; a metro view starts at the radius.
    var zoom by remember(hasActiveMetro, userInMetro) {
        mutableStateOf(
            when {
                userInMetro -> zoomFor(DEFAULT_RADIUS_KM)
                hasActiveMetro -> METRO_ZOOM
                else -> ALL_REGIONS_ZOOM
            },
        )
    }
    // Keep the camera glued to the anchor while the user hasn't panned/zoomed themselves: the
    // anchor moves as async state arrives (VM default → real metro center → location fix), and the
    // first composition happens before any of it. A manual gesture takes ownership of the camera.
    var cameraTouched by remember { mutableStateOf(false) }
    LaunchedEffect(anchor) {
        if (!cameraTouched) center = MapPoint(anchor.lat, anchor.lng)
    }

    // "Within X km" only means something around the member's OWN position. Anchored on a metro
    // centre instead it silently hid most of the metro: every GTA business in North York, Markham or
    // Richmond Hill is well outside 5 km of downtown Toronto, so the default emptied the map even
    // where the data was good. Browsing without a fix now shows the whole set instead of a slice of
    // it — bounded by MAX_PINS_WITHOUT_RADIUS, because rendering ~1,000 pins ANRs the app.
    val radiusApplies = userInMetro
    val inRadius = remember(businesses, anchor, radiusKm, radiusApplies) {
        if (radiusApplies) {
            businesses.filter { business ->
                val km = business.distanceKmFrom(anchor)
                km != null && km <= radiusKm
            }
        } else {
            businesses
                .mapNotNull { b -> b.distanceKmFrom(anchor)?.let { b to it } }
                .sortedBy { it.second }
                .take(MAX_PINS_WITHOUT_RADIUS)
                .map { it.first }
        }
    }
    val selected = inRadius.firstOrNull { it.id == selectedId }
    // Listings that share one address (a building full of law offices — ~160 on prod) cluster into
    // a bubble that no amount of zooming splits, so tapping it used to do nothing at all (owner
    // report, iPhone, 2026-09-11). The map now hands back the whole group and the member picks.
    var samePlaceIds by remember { mutableStateOf<List<String>>(emptyList()) }
    val samePlace = remember(samePlaceIds, inRadius) {
        samePlaceIds.mapNotNull { id -> inRadius.firstOrNull { it.id == id } }
    }
    if (samePlace.isNotEmpty()) {
        AppBottomSheet(
            onDismissRequest = { samePlaceIds = emptyList() },
            title = stringResource(Res.string.map_same_spot_title, lang.formatCount(samePlace.size)),
        ) {
            Text(
                text = samePlace.first().address.line.ifBlank { AppTheme.language.cityLabel(samePlace.first().address.city) },
                style = AppTheme.typography.bodySm,
                color = AppTheme.colors.onSurfaceVariant,
            )
            samePlace.forEach { business ->
                val icon = categoryIconKey(business.categoryId)
                val imageRes = categoryImageRes(business.categoryId)
                val coverUrl = business.coverUrl?.takeIf { it.isNotBlank() }
                BusinessListItem(
                    name = business.displayName(lang),
                    category = business.serviceLine(lang),
                    rating = business.rating.toFloat(),
                    newLabel = stringResource(Res.string.business_badge_new)
                        .takeIf { business.reviewCount == 0 },
                    distance = distanceLabel(business, userLocation.takeIf { userInMetro })
                        ?: AppTheme.language.cityLabel(business.address.city),
                    // Pick one → it becomes the selected pin and its card slides up on the map.
                    onClick = {
                        selectedId = business.id
                        samePlaceIds = emptyList()
                    },
                    coverIcon = icon,
                    coverImageRes = imageRes,
                    thumbnail = coverUrl?.let { url ->
                        { BusinessCover(imageUrl = url, iconName = icon, placeholderImageRes = imageRes) }
                    },
                    modifier = Modifier.padding(top = AppTheme.spacing.sm),
                )
            }
        }
    }

    Box(modifier = modifier.fillMaxSize()) {
        val clusterLabel = stringResource(Res.string.map_cluster_a11y)
        AppMap(
            center = center,
            zoom = zoom,
            onCameraChange = { c, z ->
                cameraTouched = true
                center = c
                zoom = z
            },
            pins = inRadius.mapNotNull { business ->
                business.location()?.let { loc ->
                    MapPin(id = business.id, lat = loc.lat, lng = loc.lng, selected = business.id == selectedId)
                }
            },
            onPinClick = { id -> selectedId = if (selectedId == id) null else id },
            // One pin behind the bubble → just select it; several → let the member choose.
            onClusterPinsClick = { ids ->
                if (ids.size == 1) selectedId = ids.first() else samePlaceIds = ids
            },
            attribution = stringResource(Res.string.map_attribution),
            pinContentDescription = stringResource(Res.string.map_pin_a11y),
            // Template read once here (a composable call), formatted per marker in the lambda —
            // the count varies per bubble and stringResource cannot be called from inside one.
            clusterContentDescription = { n -> clusterLabel.replace("%1\u0024d", n.toString()) },
            userLocation = userLocation?.let { MapPoint(it.lat, it.lng) },
            radiusKm = if (radiusApplies) radiusKm else null,
            radiusCenter = MapPoint(anchor.lat, anchor.lng),
            modifier = Modifier.fillMaxSize(),
        )

        // The map is deliberately full-bleed, which left the system clock and battery icons drawn
        // directly on top of street labels — unreadable, and the first thing anyone notices. Reuse
        // Home's scrim (surface strip + dark icons) so the status bar always has its own ground.
        StatusBarScrim(scrolled = true)

        // Top overlay: back + radius chips on a readable pill row.
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .statusBarsPadding()
                .padding(AppTheme.spacing.md),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
        ) {
            Box(modifier = Modifier.background(AppTheme.colors.surface, AppTheme.shapes.pill)) {
                AppIconButton(
                    iconName = "arrow_back",
                    contentDescription = stringResource(Res.string.map_back_a11y),
                    onClick = onBack,
                )
            }
            Row(
                modifier = Modifier
                    .background(AppTheme.colors.surface, AppTheme.shapes.pill)
                    .padding(AppTheme.spacing.xs)
                    .horizontalScroll(rememberScrollState()),
                horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
            ) {
                // Hidden when no radius applies ("All Canada"). Left visible they were worse than
                // inert: the handler re-centres on the anchor, so a Vancouver member browsing All
                // Canada tapped "10 km" and got teleported to downtown Toronto with no circle and
                // no pins. A control with nothing to control does not belong on screen.
                if (radiusApplies) RADIUS_OPTIONS_KM.forEach { option ->
                    AppChip(
                        label = stringResource(Res.string.map_radius_km, lang.formatCount(option.toInt())),
                        selected = radiusKm == option,
                        onClick = {
                            radiusKm = option
                            zoom = zoomFor(option)
                            center = MapPoint(anchor.lat, anchor.lng)
                            cameraTouched = false
                            selectedId = null
                        },
                    )
                }
            }
        }

        // Bottom overlay: recenter + the selected business card.
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .align(Alignment.BottomCenter)
                .navigationBarsPadding()
                .padding(AppTheme.spacing.md),
            verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
            horizontalAlignment = Alignment.End,
        ) {
            if (userLocation != null) {
                Box(modifier = Modifier.background(AppTheme.colors.surface, AppTheme.shapes.pill)) {
                    AppIconButton(
                        iconName = "my_location",
                        contentDescription = stringResource(Res.string.map_recenter_a11y),
                        onClick = {
                            center = MapPoint(anchor.lat, anchor.lng)
                            zoom = when {
                                radiusApplies -> zoomFor(radiusKm)
                                hasActiveMetro -> METRO_ZOOM
                                else -> ALL_REGIONS_ZOOM
                            }
                            cameraTouched = false
                        },
                    )
                }
            }
            if (selected != null) {
                val coverUrl = selected.coverUrl?.takeIf { it.isNotBlank() }
                val icon = categoryIconKey(selected.categoryId)
                val imageRes = categoryImageRes(selected.categoryId)
                AppCard(modifier = Modifier.fillMaxWidth()) {
                    BusinessListItem(
                        name = selected.displayName(lang),
                        category = selected.serviceLine(lang),
                        rating = selected.rating.toFloat(),
                        newLabel = stringResource(Res.string.business_badge_new)
                            .takeIf { selected.reviewCount == 0 },
                        // A remote browser gets the city, not "3,361 km" to their far-away device.
                        distance = distanceLabel(selected, userLocation.takeIf { userInMetro })
                            ?: AppTheme.language.cityLabel(selected.address.city),
                        onClick = { onBusinessClick(selected.id) },
                        coverIcon = icon,
                        coverImageRes = imageRes,
                        thumbnail = coverUrl?.let { url ->
                            { BusinessCover(imageUrl = url, iconName = icon, placeholderImageRes = imageRes) }
                        },
                    )
                }
            }
        }
    }
}

/** "2.4 km" / "850 m" for the floating card, or null without a location fix (caller shows the city). */
@Composable
private fun distanceLabel(business: Business, userLocation: GeoPoint?): String? {
    val km = userLocation?.let { business.distanceKmFrom(it) } ?: return null
    return if (km < 1.0) {
        stringResource(Res.string.business_distance_m, (km * 1000).roundToInt().toString())
    } else {
        val tenths = (km * 10).roundToInt()
        val value = if (tenths % 10 == 0) "${tenths / 10}" else "${tenths / 10}.${tenths % 10}"
        stringResource(Res.string.business_distance_km, value)
    }
}
